<script lang="ts" setup>
import { useDebounceFn } from '@vueuse/core';
import { useUserScopes } from '@/core/hooks/useUserScopes';

//#region Globals
const { state } = useStore();
const route = useRoute();
const router = useRouter();
//#endregion

//#region Debounced Navigation
const debouncedNavigate = useDebounceFn((to: any) => {
   router.push(to);
}, 300);
//#endregion

//#region Props
const props = defineProps<{
   view?: 'vertical' | 'horizontal';
}>();

const hideShowMore = defineModel<boolean>({
   default: false,
   type: Boolean,
});
//#endregion

//#region query
const space = computed(() => {
   return state.space?.space || JSON.parse(localStorage.getItem('space') || '{}');
});
const board_id = computed(
   () => route.query.board_id?.toString() || route.params.board_id?.toString() || space.value?.board_id?.toString() || undefined,
);
//#endregion

//#region scopes
const { isSuperAdmin, hasBoardView, hasBoardCommunications, hasPoolView } = useUserScopes();
//#endregion

//#region visible navigation icons
const is_boards_visible = computed(() => {
   return isSuperAdmin.value || hasBoardView.value;
});

const is_pools_visible = computed(() => {
   return isSuperAdmin.value || hasPoolView.value;
});

const is_communication_visible = computed(() => {
   return isSuperAdmin.value || hasBoardCommunications.value;
});

const is_dashboard_visible = computed(() => {
   return isSuperAdmin.value || hasBoardView.value;
});

const has_logent_dashboard = computed(() => {
   return state.user?.user_info?.tenant?.key === 'logent';
});
//#endregion
</script>

<template>
   <a
      v-if="is_boards_visible"
      href="#"
      :class="[
         'router-link',
         {
            active: ['board-details-planning', 'board-details-overview', 'board-details', 'board-overview'].includes(
               $route.name!.toString(),
            ),
         },
      ]"
      @click.prevent="
         debouncedNavigate(
            board_id
               ? { name: 'board-details', params: { board_id }, query: { board_id } }
               : { name: 'board-overview', query: { board_id }, params: { board_id } },
         )
      "
   >
      <i class="fas fa-table-list" />
      <span class="navigation-links-item__label">{{ space?.name ?? $t('global.boards', 2) }}</span>
   </a>
   <a
      v-if="is_pools_visible"
      href="#"
      :class="['router-link', { active: $route.path === '/pool' }]"
      @click.prevent="debouncedNavigate({ path: '/pool', query: { board_id } })"
   >
      <i class="fas fa-folder-user" />
      <span class="navigation-links-item__label">{{ $t('global.pools', 2) }}</span>
   </a>

   <template v-if="view !== 'horizontal'">
      <a
         v-if="is_communication_visible"
         href="#"
         :class="['router-link', { active: $route.path === '/communication' }]"
         @click.prevent="debouncedNavigate({ path: '/communication', query: { board_id } })"
      >
         <i class="fas fa-messages" />
         <span class="navigation-links-item__label">{{ $t('global.communication') }}</span>
      </a>
      <a
         v-if="is_dashboard_visible"
         href="#"
         :class="['router-link', { active: $route.path === '/dashboard' }]"
         @click.prevent="debouncedNavigate({ path: '/dashboard', query: { board_id } })"
      >
         <i class="fas fa-gauge-high" />
         <span class="navigation-links-item__label">{{ $t('global.dashboard') }}</span>
      </a>
      <a
         v-if="is_dashboard_visible && has_logent_dashboard"
         href="#"
         :class="['router-link', { active: $route.path === '/logent-dashboard' }]"
         @click.prevent="debouncedNavigate({ path: '/logent-dashboard', query: { board_id } })"
      >
         <i class="fas fa-gauge-high" />
         <span class="navigation-links-item__label">{{ `Logent ${$t('global.dashboard')}` }}</span>
      </a>
      <!-- <a
         v-if="is_customer_visible"
         href="#"
         :class="['router-link', { active: $route.name === 'customer-overview' }]"
         @click.prevent="debouncedNavigate({ name: 'customer-overview', query: { board_id } })"
      >
         <i class="fas fa-buildings" />
         <span class="navigation-links-item__label">{{ $t('global.customer', 2) }}</span>
      </a> -->
   </template>

   <a
      v-if="view === 'horizontal' && space?.board_id"
      href="#"
      :class="['router-link', { active: ['board-details-timeregistrations'].includes($route.name!.toString()) }]"
      @click.prevent="
         debouncedNavigate({
            name: 'board-details-timeregistrations',
            query: { board_id: space.board_id },
            params: { board_id: space.board_id },
         })
      "
   >
      <i class="fas fa-timer" />
      <span class="navigation-links-item__label">{{ $t('global.timeregistration', 2) }}</span>
   </a>

   <a
      v-if="space?.board_id"
      href="#"
      :class="['router-link', { active: $route.name === 'time-clock' }]"
      @click.prevent="debouncedNavigate({ name: 'time-clock', query: { board_id: space.board_id } })"
   >
      <i class="fas fa-qrcode" />
      <span>{{ $t('badge.scan') }}</span>
   </a>
</template>

<style lang="scss" scoped>
.navigation-links-item__label {
   white-space: nowrap;
   overflow: hidden;
   text-overflow: ellipsis;
}

.router-link {
   display: flex;
   align-items: center;
   text-decoration: none;
   cursor: pointer;
   color: inherit;
}
</style>
